import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Printer, X } from 'lucide-react';
import {
  useCalibrationCertificateDetail,
} from '../hooks/useCalibrationCertificates';
import type { CalibrationCertificateDetail } from '../types';

interface CalibrationCertificateDialogProps {
  mongoId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(dateStr: string | null | undefined, locale = 'fr-CA'): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString(locale);
}

function localeFor(language: string): string {
  return language && language.toLowerCase().startsWith('en') ? 'en-CA' : 'fr-CA';
}

function formatNumber(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return '--';
  return value.toFixed(decimals);
}

function CertificateContent({ data }: { data: CalibrationCertificateDetail }) {
  const { t, i18n } = useTranslation('intelligence');
  const locale = localeFor(i18n.language);
  const fd = (s: string | null | undefined) => formatDate(s, locale);
  const calibrator = data.calibrators?.[0];

  return (
    <div className="certificate-printable space-y-6 text-sm">
      {/* Page 1: Header & Basic Info */}
      <div className="page-1">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('calibration.print.title', "CERTIFICAT D'ÉTALONNAGE")}
            </h1>
            <p className="text-lg text-gray-600 mt-1">{data.cert_number}</p>
          </div>
          <div className="text-right">
            <img
              src="/atek/dist/Logo-Address.png"
              alt="ATEK"
              className="h-16 ml-auto"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <p className="text-xs text-gray-500 mt-2">240 - 5490 boul Thimens</p>
            <p className="text-xs text-gray-500">St-Laurent, QC, H4R 2K9</p>
            <p className="text-xs text-gray-500">1-855-982-2835</p>
          </div>
        </div>

        {/* Certificate Info */}
        <table className="w-full border-collapse mb-6">
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-medium text-gray-700 w-1/3">
                {t('calibration.print.certNumber', 'Numéro de certificat')}
              </td>
              <td className="py-2">{data.cert_number}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-medium text-gray-700">
                {t('calibration.print.revision', 'Révision')}
              </td>
              <td className="py-2">{data.revision || 0}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-medium text-gray-700">
                {t('calibration.print.issueDate', 'Date de production du certificat')}
              </td>
              <td className="py-2">{fd(data.issue_date)}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-medium text-gray-700">
                {t('calibration.print.dueDate', "Date d'échéance du certificat")}
              </td>
              <td className="py-2 font-medium">{fd(data.due_date)}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-medium text-gray-700">
                {t('calibration.print.technician', "Nom du responsable de l'étalonnage")}
              </td>
              <td className="py-2">{data.technician?.name || '--'}</td>
            </tr>
            {data.calibration_procedure?.description && (
              <tr className="border-b">
                <td className="py-2 font-medium text-gray-700 align-top">
                  {t('calibration.print.procedure', "Description de la procédure d'étalonnage")}
                </td>
                <td className="py-2">{data.calibration_procedure.description}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Customer Info */}
        <h3 className="font-semibold text-gray-800 mb-2 border-b pb-1">
          {t('calibration.print.customerInfo', 'Informations sur le client')}
        </h3>
        <table className="w-full border-collapse mb-6 border">
          <tbody>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50 w-1/3">
                {t('calibration.print.organisation', "Nom de l'organisation")}
              </td>
              <td className="py-2 px-3">{data.organisation?.name || '--'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.site', 'Nom du site')}
              </td>
              <td className="py-2 px-3">{data.site?.name || '--'}</td>
            </tr>
            <tr>
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.siteAddress', 'Adresse du site')}
              </td>
              <td className="py-2 px-3">{data.site?.address || '--'}</td>
            </tr>
          </tbody>
        </table>

        {/* Equipment Info */}
        <h3 className="font-semibold text-gray-800 mb-2 border-b pb-1">
          {t('calibration.print.equipmentInfo', "Informations sur l'équipement où se trouve le capteur à étalonner")}
        </h3>
        <table className="w-full border-collapse mb-6 border">
          <tbody>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50 w-1/3">
                {t('calibration.print.sensorName', 'Nom du capteur à étalonner')}
              </td>
              <td className="py-2 px-3">{data.calibrated_device?.name || '--'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.location', "Emplacement de l'équipement")}
              </td>
              <td className="py-2 px-3">{data.location?.name || '--'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.equipmentType', "Type d'équipement")}
              </td>
              <td className="py-2 px-3">{data.equipment?.type || '--'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.manufacturer', "Manufacturier de l'équipement")}
              </td>
              <td className="py-2 px-3">{data.equipment?.manufacturer || '--'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.model', "Modèle de l'équipement")}
              </td>
              <td className="py-2 px-3">{data.equipment?.model || '--'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.serialNumber', "Numéro de série de l'équipement")}
              </td>
              <td className="py-2 px-3">{data.equipment?.serial_number || '--'}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.inputNumber', 'Input #')}
              </td>
              <td className="py-2 px-3">{data.equipment?.input_number || '--'}</td>
            </tr>
            <tr>
              <td className="py-2 px-3 font-medium text-gray-700 bg-gray-50">
                {t('calibration.print.customId', "ID client de l'équipement")}
              </td>
              <td className="py-2 px-3">{data.equipment?.custom_id || '--'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Page 2: Calibrator & Device Comparison */}
      <div className="page-2 break-before-page">
        <h3 className="font-semibold text-gray-800 mb-3 border-b pb-1">
          {t('calibration.print.devicesTitle', "Informations sur les instruments d'étalonnage")}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {/* Calibrator Column */}
          <div className="border rounded">
            <div className="bg-gray-100 px-3 py-2 font-semibold border-b">
              {t('calibration.print.calibrator', 'Étalon')}
            </div>
            <table className="w-full text-xs">
              <tbody>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.name', 'Nom')}</td>
                  <td className="py-1.5 px-2">{calibrator?.name || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.manufacturer', 'Manufacturier')}</td>
                  <td className="py-1.5 px-2">{calibrator?.manufacturer || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.model', 'Modèle')}</td>
                  <td className="py-1.5 px-2">{calibrator?.model || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.serial', 'Numéro de série')}</td>
                  <td className="py-1.5 px-2">{calibrator?.serial_number || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.unit', 'Unité')}</td>
                  <td className="py-1.5 px-2">{calibrator?.unit || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.resolution', 'Résolution')}</td>
                  <td className="py-1.5 px-2">{calibrator?.resolution || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.uncertainty', 'Incertitude')}</td>
                  <td className="py-1.5 px-2">{calibrator?.measurement_uncertainty || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.prevCert', 'Cert. précédent')}</td>
                  <td className="py-1.5 px-2">{calibrator?.cert_number || '--'}</td>
                </tr>
                <tr>
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.certDueDate', 'Échéance cert.')}</td>
                  <td className="py-1.5 px-2">{fd(calibrator?.cert_due_date)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Calibrated Device Column */}
          <div className="border rounded">
            <div className="bg-gray-100 px-3 py-2 font-semibold border-b">
              {t('calibration.print.calibratedDevice', 'Capteur à étalonner')}
            </div>
            <table className="w-full text-xs">
              <tbody>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.name', 'Nom')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.name || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.manufacturer', 'Manufacturier')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.manufacturer || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.model', 'Modèle')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.model || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.serial', 'Numéro de série')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.serial_number || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.unit', 'Unité')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.unit || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.resolution', 'Résolution')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.resolution || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.uncertainty', 'Incertitude')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.measurement_uncertainty || '--'}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.prevCert', 'Cert. précédent')}</td>
                  <td className="py-1.5 px-2">{data.calibrated_device?.cert_number || '--'}</td>
                </tr>
                <tr>
                  <td className="py-1.5 px-2 font-medium text-gray-600">{t('calibration.print.certDueDate', 'Échéance cert.')}</td>
                  <td className="py-1.5 px-2">{fd(data.calibrated_device?.cert_due_date)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Page 3: Measurement Results */}
      {data.measurement_results && data.measurement_results.length > 0 && (
        <div className="page-3 break-before-page">
          <h3 className="font-semibold text-gray-800 mb-3 border-b pb-1">
            {t('calibration.print.measurementResults', 'Résultats des mesures')}
          </h3>

          {/* Environment conditions */}
          {data.environment && (
            <p className="text-xs text-gray-600 mb-3">
              {t('calibration.print.environment', 'Conditions environnementales')}:{' '}
              {t('calibration.print.temperature', 'Température')}: {data.environment.temperature ?? 22}°C
              ±{data.environment.temperature_tolerance ?? 4}°C,{' '}
              {t('calibration.print.humidity', 'Humidité')}: {data.environment.humidity ?? 40}%
              ±{data.environment.humidity_tolerance ?? 15}%
            </p>
          )}

          {/* As Received */}
          <h4 className="font-medium text-gray-700 mb-2 text-sm">
            {t('calibration.print.asReceived', 'Mesures à la réception')}
          </h4>
          <table className="w-full border-collapse border text-xs mb-4">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1.5 text-left">{t('calibration.print.measurand', 'Mesurande')}</th>
                <th className="border px-2 py-1.5 text-right">{t('calibration.print.reference', 'Valeur de référence')}</th>
                <th className="border px-2 py-1.5 text-right">{t('calibration.print.measured', 'Valeur mesurée')}</th>
                <th className="border px-2 py-1.5 text-right">{t('calibration.print.error', 'Erreur')}</th>
                <th className="border px-2 py-1.5 text-right">{t('calibration.print.tolerance', 'Tolérance')}</th>
                <th className="border px-2 py-1.5 text-center">{t('calibration.print.validation', 'Conforme')}</th>
              </tr>
            </thead>
            <tbody>
              {data.measurement_results.map((m, idx) => (
                <tr key={idx}>
                  <td className="border px-2 py-1.5">{m.measurand || `${t('calibration.print.point', 'Point')} ${idx + 1}`}</td>
                  <td className="border px-2 py-1.5 text-right font-mono">{formatNumber(m.reference_quantity_value)}</td>
                  <td className="border px-2 py-1.5 text-right font-mono">{formatNumber(m.measured_quantity_value)}</td>
                  <td className="border px-2 py-1.5 text-right font-mono">{formatNumber(m.measurement_error)}</td>
                  <td className="border px-2 py-1.5 text-right font-mono">±{formatNumber(m.target_measurement_uncertainty)}</td>
                  <td className={`border px-2 py-1.5 text-center font-medium ${m.validation ? 'text-green-600' : 'text-red-600'}`}>
                    {m.validation ? t('calibration.print.yes', 'YES') : t('calibration.print.no', 'NO')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Calibration Curve */}
          {(data.previous_calibration_curve || data.next_calibration_curve) && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 mb-2 text-sm">
                {t('calibration.print.calibrationCurve', "Courbe d'étalonnage (y = ax³ + bx² + cx + d)")}
              </h4>
              <table className="w-full border-collapse border text-xs">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-1.5"></th>
                    <th className="border px-2 py-1.5 text-center">a</th>
                    <th className="border px-2 py-1.5 text-center">b</th>
                    <th className="border px-2 py-1.5 text-center">c</th>
                    <th className="border px-2 py-1.5 text-center">d</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-2 py-1.5 font-medium bg-gray-50">{t('calibration.print.before', 'Avant')}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.previous_calibration_curve?.a, 6)}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.previous_calibration_curve?.b, 6)}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.previous_calibration_curve?.c, 6)}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.previous_calibration_curve?.d, 6)}</td>
                  </tr>
                  <tr>
                    <td className="border px-2 py-1.5 font-medium bg-gray-50">{t('calibration.print.after', 'Après')}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.next_calibration_curve?.a, 6)}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.next_calibration_curve?.b, 6)}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.next_calibration_curve?.c, 6)}</td>
                    <td className="border px-2 py-1.5 text-center font-mono">{formatNumber(data.next_calibration_curve?.d, 6)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* As Delivered (Adjusted) */}
          {data.adjusted_measurement_results && data.adjusted_measurement_results.length > 0 && (
            <>
              <h4 className="font-medium text-gray-700 mb-2 text-sm">
                {t('calibration.print.asDelivered', 'Mesures à la livraison (corrigées)')}
              </h4>
              <table className="w-full border-collapse border text-xs mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-1.5 text-left">{t('calibration.print.measurand', 'Mesurande')}</th>
                    <th className="border px-2 py-1.5 text-right">{t('calibration.print.reference', 'Valeur de référence')}</th>
                    <th className="border px-2 py-1.5 text-right">{t('calibration.print.measured', 'Valeur mesurée')}</th>
                    <th className="border px-2 py-1.5 text-right">{t('calibration.print.error', 'Erreur')}</th>
                    <th className="border px-2 py-1.5 text-right">{t('calibration.print.tolerance', 'Tolérance')}</th>
                    <th className="border px-2 py-1.5 text-center">{t('calibration.print.validation', 'Conforme')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.adjusted_measurement_results.map((m, idx) => (
                    <tr key={idx}>
                      <td className="border px-2 py-1.5">{m.measurand || `${t('calibration.print.point', 'Point')} ${idx + 1}`}</td>
                      <td className="border px-2 py-1.5 text-right font-mono">{formatNumber(m.reference_quantity_value)}</td>
                      <td className="border px-2 py-1.5 text-right font-mono">{formatNumber(m.measured_quantity_value)}</td>
                      <td className="border px-2 py-1.5 text-right font-mono">{formatNumber(m.measurement_error)}</td>
                      <td className="border px-2 py-1.5 text-right font-mono">±{formatNumber(m.target_measurement_uncertainty)}</td>
                      <td className={`border px-2 py-1.5 text-center font-medium ${m.validation ? 'text-green-600' : 'text-red-600'}`}>
                        {m.validation ? t('calibration.print.yes', 'YES') : t('calibration.print.no', 'NO')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Comments */}
          {data.comment && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 mb-1 text-sm">
                {t('calibration.print.comments', 'Commentaires')}
              </h4>
              <p className="text-xs border p-2 bg-gray-50 min-h-[40px]">{data.comment}</p>
            </div>
          )}

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-8 mt-8 pt-4">
            <div>
              <div className="border-b border-gray-400 h-8 mb-1"></div>
              <p className="text-xs text-gray-600">{t('calibration.print.technicianSignature', 'Technicien')}:</p>
              <p className="text-sm">{data.technician?.name || '--'}</p>
              <p className="text-xs text-gray-500">{fd(data.calibration_date)}</p>
            </div>
            <div>
              <div className="border-b border-gray-400 h-8 mb-1"></div>
              <p className="text-xs text-gray-600">{t('calibration.print.verifierSignature', 'Vérificateur')}:</p>
              <p className="text-sm">{data.verified_by?.name || '--'}</p>
              <p className="text-xs text-gray-500">{fd(data.verified_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CalibrationCertificateDialog({
  mongoId,
  open,
  onOpenChange,
}: CalibrationCertificateDialogProps) {
  const { t } = useTranslation('intelligence');
  const printRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useCalibrationCertificateDetail(mongoId);

  const handlePrint = () => {
    if (!printRef.current || !data) return;

    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('calibration.print.dialogTitle', 'Print')} ${data.cert_number}</title>
          <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet">
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: 'Open Sans', sans-serif;
              font-size: 11px;
              line-height: 1.4;
              padding: 0.5in 0.75in;
              color: #1a1a1a;
            }
            table { border-collapse: collapse; width: 100%; }
            td, th { padding: 4px 8px; }
            .border { border: 1px solid #333; }
            .border-b { border-bottom: 1px solid #ddd; }
            .bg-gray-50 { background: #f9fafb; }
            .bg-gray-100 { background: #f3f4f6; }
            .font-bold { font-weight: 700; }
            .font-semibold { font-weight: 600; }
            .font-medium { font-weight: 500; }
            .font-mono { font-family: monospace; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .text-left { text-align: left; }
            .text-xs { font-size: 10px; }
            .text-sm { font-size: 11px; }
            .text-lg { font-size: 14px; }
            .text-2xl { font-size: 20px; }
            .text-gray-500 { color: #6b7280; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-700 { color: #374151; }
            .text-gray-800 { color: #1f2937; }
            .text-gray-900 { color: #111827; }
            .text-green-600 { color: #059669; }
            .text-red-600 { color: #dc2626; }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mb-3 { margin-bottom: 12px; }
            .mb-4 { margin-bottom: 16px; }
            .mb-6 { margin-bottom: 24px; }
            .mt-1 { margin-top: 4px; }
            .mt-2 { margin-top: 8px; }
            .mt-8 { margin-top: 32px; }
            .py-1 { padding-top: 4px; padding-bottom: 4px; }
            .py-1\\.5 { padding-top: 6px; padding-bottom: 6px; }
            .py-2 { padding-top: 8px; padding-bottom: 8px; }
            .px-2 { padding-left: 8px; padding-right: 8px; }
            .px-3 { padding-left: 12px; padding-right: 12px; }
            .p-2 { padding: 8px; }
            .pb-1 { padding-bottom: 4px; }
            .pt-4 { padding-top: 16px; }
            .h-8 { height: 32px; }
            .h-16 { height: 64px; }
            .w-1\\/3 { width: 33.333%; }
            .min-h-\\[40px\\] { min-height: 40px; }
            .space-y-6 > * + * { margin-top: 24px; }
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
            .gap-4 { gap: 16px; }
            .gap-8 { gap: 32px; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .items-start { align-items: flex-start; }
            .ml-auto { margin-left: auto; }
            .rounded { border-radius: 4px; }
            .align-top { vertical-align: top; }
            .break-before-page { page-break-before: always; }
            h1 { font-size: 20px; font-weight: bold; }
            h3 { font-size: 13px; }
            h4 { font-size: 12px; }
            img { max-height: 64px; }
            @media print {
              body { padding: 0; }
              .break-before-page { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0 flex flex-row items-center justify-between pr-8">
          <DialogTitle className="flex items-center gap-2">
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              <>
                {t('calibration.print.dialogTitle', 'Imprimer')} {data?.cert_number} {data?.calibrated_device?.name}
              </>
            )}
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              disabled={isLoading || !data}
            >
              <Printer className="h-4 w-4 mr-2" />
              {t('calibration.print.print', 'Imprimer')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4 mr-2" />
              {t('calibration.print.close', 'Fermer')}
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto border rounded-lg bg-white p-6" ref={printRef}>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              {t('calibration.print.loadError', 'Failed to load the certificate')}
            </div>
          ) : data ? (
            <CertificateContent data={data} />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
